import { useState, useCallback } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Divider,
  CircularProgress,
  Checkbox,
  ListItemText,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ToolDialog from "../ToolDialog";

/**
 * ExportKmlTool
 * ToolBox-compatible: { title, map, layers, onClose }
 *
 * layers: { layer_id: { name, id, vectorLayer, geometry_types } }
 * Calls GET /export_kml?layer_ids=<id1>&layer_ids=<id2>...
 */
export default function ExportKmlTool({ title, map, layers, onClose }) {
  const [selectedLayerIds, setSelectedLayerIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const layerEntries = Object.entries(layers ?? {});

  const handleExport = useCallback(async () => {
    if (!selectedLayerIds.length) return;

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();

      selectedLayerIds.forEach((layerId) => {
        params.append("layer_ids", layerId);
      });

      const url = `https://app.mapgeoid.com/api-service/feature_service/export_kml?${params.toString()}`;
      const res = await fetch(url);

      if (!res.ok) {
        let errorMessage = `Server error: ${res.status}`;

        try {
          const errorData = await res.json();
          if (errorData?.detail) {
            errorMessage = errorData.detail;
          }
        } catch (e) {
          // keep fallback message
        }

        throw new Error(errorMessage);
      }

      const blob = await res.blob();
      const downloadUrl = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download =
        selectedLayerIds.length === 1
          ? `layer_${selectedLayerIds[0]}.kml`
          : "layers_export.kml";

      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      setError(err.message || "Failed to export KML");
    } finally {
      setLoading(false);
    }
  }, [selectedLayerIds]);

  const handleSelectAll = () => {
    setSelectedLayerIds(layerEntries.map(([lid]) => lid));
    setError(null);
  };

  const handleClearAll = () => {
    setSelectedLayerIds([]);
    setError(null);
  };

  return (
    <ToolDialog title={title} open={true} onClose={onClose}>
      <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 2 }}>
        <FormControl fullWidth size="small">
          <InputLabel>Select Layer(s)</InputLabel>
          <Select
            multiple
            open={dropdownOpen}
            value={selectedLayerIds}
            label="Select Layer(s)"
            onOpen={() => setDropdownOpen(true)}
            onClose={() => {}}
            onChange={(e) => {
              setSelectedLayerIds(e.target.value);
              setError(null);
            }}
            renderValue={(selected) =>
              selected.map((id) => layers?.[id]?.name || id).join(", ")
            }
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 360,
                },
              },
            }}
          >
            {layerEntries.map(([lid, linfo]) => (
              <MenuItem key={lid} value={lid}>
                <Checkbox checked={selectedLayerIds.indexOf(lid) > -1} />
                <ListItemText
                  primary={linfo.name}
                  secondary={
                    Array.isArray(linfo.geometry_types)
                      ? linfo.geometry_types.join(", ")
                      : ""
                  }
                />
              </MenuItem>
            ))}

            <Divider />

            <Box
              sx={{
                p: 1,
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                justifyContent: "space-between",
              }}
            >
              <Button
                size="small"
                variant="outlined"
                onClick={handleSelectAll}
                sx={{ textTransform: "none" }}
              >
                Select All
              </Button>

              <Button
                size="small"
                variant="outlined"
                onClick={handleClearAll}
                sx={{ textTransform: "none" }}
              >
                Clear All
              </Button>

              <Button
                size="small"
                variant="contained"
                onClick={() => setDropdownOpen(false)}
                sx={{ textTransform: "none" }}
              >
                OK
              </Button>
            </Box>
          </Select>
        </FormControl>

        {error && (
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        )}

        <Divider />

        <Button
          variant="contained"
          size="small"
          startIcon={
            loading ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <DownloadIcon />
            )
          }
          onClick={handleExport}
          disabled={!selectedLayerIds.length || loading}
          sx={{ textTransform: "none" }}
        >
          {loading ? "Exporting..." : "Export KML"}
        </Button>
      </Box>
    </ToolDialog>
  );
}